// src/Components/Luna/LunaPowerHost.tsx
// Mounted once inside CharacterInventoryProvider. Seeds the Luna power store
// with the equipped Luna, keeps the sound setting in step with the account,
// draws the stardust flights and shows the evolution celebration when no
// activity is holding it.
import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CharacterInventoryContext } from "../../Context/CharacterInventoryContext";
import UserContext from "../../Context/UserContext";
import { soundManager } from "../../utils/SoundManager";
import LunaBurstLayer from "./LunaBurstLayer";
import LunaEvolution, { StageTile } from "./LunaEvolution";
import { currentCelebration, dismissCelebration, seedLuna } from "./lunaPowerStore";
import { stageImageOf, stageNameOf, useInventoryLuna, useLunaStore } from "./useLuna";

const LunaPowerHost: React.FC = () => {
  const inv = useContext(CharacterInventoryContext);
  const user = useContext(UserContext)?.user as any;
  const navigate = useNavigate();
  const equipped = useInventoryLuna();
  const store = useLunaStore();
  const celebration = currentCelebration(store);

  useEffect(() => {
    if (equipped) seedLuna(equipped);
  }, [equipped]);

  const audio = user?.settings?.audioEnabled;
  useEffect(() => {
    if (typeof audio === "boolean") soundManager.setEnabled(audio);
  }, [audio]);

  const stages: StageTile[] = useMemo(() => {
    if (!celebration || !inv) return [];
    const def = inv.creatureDefinitions.find((d) => d.creatureId === celebration.creatureId);
    const owned = inv.ownedCreatureMap[celebration.creatureId];
    const count = def?.stageAssets?.length ? Math.max(...def.stageAssets.map((a) => Number(a.stage) || 1)) : celebration.stage;
    const collected = new Set<number>([...(owned?.stagesCollected || []), ...Array.from({ length: celebration.stage }, (_, i) => i + 1)]);
    return Array.from({ length: count }, (_, i) => {
      const stage = i + 1;
      return {
        stage,
        name: stageNameOf(def, stage),
        image: stage === celebration.stage ? celebration.image : stage === celebration.fromStage ? celebration.fromImage : stageImageOf(def, stage),
        collected: collected.has(stage),
      };
    });
  }, [celebration, inv]);

  const close = useCallback(() => {
    if (celebration) dismissCelebration(celebration.id);
  }, [celebration]);
  const openCollection = useCallback(() => {
    if (celebration) dismissCelebration(celebration.id);
    navigate("/collection?tab=lunas");
  }, [celebration, navigate]);

  return (
    <>
      <LunaBurstLayer />
      {celebration && <LunaEvolution key={celebration.id} event={celebration} stages={stages} onClose={close} onOpenCollection={openCollection} />}
    </>
  );
};

export default LunaPowerHost;
